import {
  getTemplates,
  getTemplate,
  restoreTemplate,
  copyTemplate,
  getTemplateLevelDetail,
  updateTemplateLevel,
  deleteTemplateLevel,
  deleteTemplate,
  restoreTemplateLevel
} from '@/api/template'

/**
 * @description : 透過 id 找到陣列裡對應的 index
 * @param {Array} array : 處理的陣列
 * @param {String} id : 想要找的 id
 */
function findTemplateIndexById (array = [], id = '') {
  if (array.length === 0 && !id) return -1
  return array.findIndex(item => item.id === id)
}
/**
 * @description : 把陣列裡某項移到陣列的最前面
 * @param {Array} array : 處理的陣列
 * @param {Number} index : 移動項的 index
 */
function moveTemplateToFront (array = [], index = null) {
  if (array.length === 0 && !index) return array
  const [ object ] = array.splice(index, 1)
  array.unshift(object)
  return array
}

const getDefaultState = () => (
  {
    templateList: [],
    loadedTime: 0,
    currentTemplate: {
      id: '',
      title: '',
      advice: '',
      careTips: [],
      level: [],
      mustKnow: [],
      situations: [],
      currentLv: 1,
      isDefault: false,
      isFinish: false,
      medicalId: '',
      publishTime: 0,
      updateTime: 0
    },
    loadedInfo: {
      loadedTime: 0,
      loadedLevel: []
    },
    levelCache: null
  }
)

const state = getDefaultState()

const mutations = {
  // --- template list mutations ---
  SET_TEMPLATE_LIST: (state, templateList) => {
    state.templateList = templateList
  },
  SET_TEMPLATE_LIST_LOADED_TIME: (state, loadedTime) => {
    state.loadedTime = loadedTime
  },
  SET_LOADED_INFO: (state) => {
    state.loadedInfo.loadedLevel = []
  },
  INSERT_TEMPLATE: (state, { id, name, levelCount, isDefault, isFinish }) => {
    state.templateList.unshift({
      id: id,
      name: name,
      levelCount: levelCount,
      isDefault: isDefault,
      isFinish: isFinish,
      updateTime: new Date().getTime()
    })
  },
  UPDATE_TEMPLATE_LIST_BY_ID: (state, {id, name, isDefault}) => {
    const newTemplateList = state.templateList.map(template => {
      // 如果有設置默認模版，所有的模版的 isDefault 改為 false
      if (isDefault) {
        template.isDefault = false
      }
      // 改動對應模版
      if (template.id === id) {
        template.name = name
        template.isDefault = isDefault
        template.updateTime = new Date().getTime()
      }
      return template
    })
    state.templateList = moveTemplateToFront(newTemplateList, findTemplateIndexById(state.templateList, id))
  },
  UNSHIFT_TEMPLATE: (state, { id, name, levelCount, isDefault, isFinish, updateTime }) => {
    state.templateList.unshift({
      id: id,
      name: name,
      levelCount: levelCount,
      isDefault: isDefault,
      isFinish: isFinish,
      updateTime: updateTime
    })
  },
  DELETE_TEMPLATE: (state, { deleteTemplateId = null, defaultScheduleId = null }) => {
    const index = findTemplateIndexById(state.templateList, deleteTemplateId)
    state.templateList.splice(index, 1)
    for (let template of state.templateList) {
      if (template.id === defaultScheduleId) {
        template.isDefault = true
      }
    }
  },
  RESET_TEMPLATE_STATUS_AFTER_UPDATING: (state, { id, levelCount, isFinish, updateTime }) => {
    const index = findTemplateIndexById(state.templateList, id)

    if (index > -1) {
      state.templateList[index].levelCount = levelCount
      state.templateList[index].isFinish = isFinish
      state.templateList[index].updateTime = updateTime
      state.templateList = moveTemplateToFront(state.templateList, index)
    }
  },

  // --- current template mutations ---
  SET_CURRENT_TEMPLATE_ID: (state, currentTemplateId) => {
    state.currentTemplate.id = currentTemplateId
  },
  SET_CURRENT_TEMPLATE: (state, currentTemplate) => {
    state.currentTemplate = Object.assign(state.currentTemplate, currentTemplate)
  },
  SET_CURRENT_TEMPLATE_LEVEL_BY_LEVEL: (state, { level = null, levelData = null }) => {
    if (!level && String(level) !== '0') return
    if (!levelData) return
    state.currentTemplate.level[level - 1] = levelData
    state.loadedInfo.loadedLevel.push(level)
  },
  CREATE_CURRENT_TEMPLATE_LEVEL_BY_INDEX: (state, { levelData = null }) => {
    if (!levelData) return
    state.currentTemplate.level.push(levelData)
  },
  UPDATE_CURRENT_TEMPLATE_LEVEL_BY_INDEX: (state, { index = null, levelData = null }) => {
    if (!index && String(index) !== '0') return
    if (!levelData) return
    state.currentTemplate.level[index] = levelData
  },
  REMOVE_CURRENT_TEMPLATE_LEVEL_BY_INDEX: (state, { index = null }) => {
    if (!index && String(index) !== '0') return
    state.currentTemplate.level.splice(index, 1)
    for (let i = index; i < state.currentTemplate.level.length; i++) {
      state.currentTemplate.level[i].level = state.currentTemplate.level[i].level - 1
    }
    state.loadedInfo.loadedLevel = []
  },
  UPDATE_LEVEL_OF_TEMPLATE (state, {template, level}) {
    state.currentTemplate.level[level] = template
  },
  UPDATE_CURRENT_TEMPLATE_TITLE (state, title) {
    state.currentTemplate.title = title
  },
  SET_LEVEL_CACHE (state, levelCache) {
    state.levelCache = levelCache
  },
  RESET_LEVEL_CACHE (state) {
    state.levelCache = null
  },
  // 重置 state
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState())
  }
}

const actions = {
  // get template list
  setTemplateList ({ commit }) {
    return new Promise((resolve, reject) => {
      getTemplates()
        .then(response => {
          const { data = [] } = response

          if (data.length === 0) {
            reject(new Error('[CZN error]: Template list is empty ! please check the response data.'))
          }

          commit('SET_TEMPLATE_LIST_LOADED_TIME', new Date().getTime())
          commit('SET_TEMPLATE_LIST', data)
          resolve()
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  // get current template data
  setCurrentTemplate ({ commit }, currentTemplateId = null) {
    return new Promise((resolve, reject) => {
      if (currentTemplateId) {
        commit('SET_CURRENT_TEMPLATE_ID', currentTemplateId)
        getTemplate(currentTemplateId, 1)
          .then(response => {
            const { data } = response

            if (!data) {
              reject(new Error('[CZN error]: Current template has no data ! please check the response.'))
            }

            // // NOTE: 合併改動參數 (rehabInfos) 到 exercises
            // data.level.forEach(exer => {
            //   exer.exercisesTmp = handleMergeExerciseAndConfig(exer.exercisesTmp, exer.rehabInfos)
            // })

            commit('SET_LOADED_INFO')
            commit('SET_CURRENT_TEMPLATE', data)
            resolve(data)
          })
          .catch(error => {
            reject(error)
          })
      } else {
        reject(new Error('[CZN error]: Current template id is null'))
      }
    })
  },

  copyTemplate ({ commit }, { templateId = null, title = null }) {
    return new Promise((resolve, reject) => {
      if (!templateId) reject(new Error('[CZN error]: templateId is null'))
      copyTemplate(templateId, title)
        .then(res => {
          const { templateId, isFinish, levelCount, updateTime } = res.data

          commit('UNSHIFT_TEMPLATE', {
            id: templateId,
            name: title,
            isFinish: isFinish,
            isDefault: false,
            levelCount: levelCount,
            updateTime: updateTime
          })

          resolve(templateId)
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  deleteTemplate ({ commit }, templateId) {
    return new Promise((resolve, reject) => {
      if (!templateId) reject(new Error('[CZN error]: templateId is null'))
      deleteTemplate(templateId)
        .then(res => {
          const { defaultScheduleId } = res.data
          commit('DELETE_TEMPLATE', {
            deleteTemplateId: templateId,
            defaultScheduleId: defaultScheduleId
          })
          resolve()
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  restoreTemplate ({ commit }, templateId) {
    return new Promise((resolve, reject) => {
      if (!templateId) reject(new Error('[CZN error]: templateId is null'))
      restoreTemplate(templateId)
        .then(res => {
          const { id, name, isFinish, isDefault, levelCount, updateTime } = res.data

          commit('UNSHIFT_TEMPLATE', {
            id: id,
            name: name,
            isFinish: isFinish,
            isDefault: isDefault,
            levelCount: levelCount,
            updateTime: updateTime
          })

          resolve(templateId)
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  setCurrentTemplateLevelDetail ({ commit, state }, level) {
    if (state.loadedInfo.loadedLevel.includes(level)) return
    return new Promise((resolve, reject) => {
      getTemplateLevelDetail(state.currentTemplate.id, level)
        .then(response => {
          const levelData = response.data

          commit('SET_CURRENT_TEMPLATE_LEVEL_BY_LEVEL', { level, levelData })

          resolve(levelData)
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  // update template list data
  updateTemplateListById ({ commit }, {id, name, isDefault} = {}) {
    return new Promise((resolve, reject) => {
      commit('')
    })
  },

  createLevelOfCurrentTemplate ({ commit, state }, { modifyLevelData } = {}) {
    return new Promise((resolve) => {
      updateTemplateLevel(state.currentTemplate.id, modifyLevelData)
        .then(res => {
          const { level: levelCache, levelCount, isFinish, updateTime } = res.data
          commit('CREATE_CURRENT_TEMPLATE_LEVEL_BY_INDEX', { levelData: modifyLevelData })
          commit('RESET_TEMPLATE_STATUS_AFTER_UPDATING', {
            id: state.currentTemplate.id,
            levelCount: levelCount,
            isFinish: isFinish,
            updateTime: updateTime
          })
          resolve()
        })
    })
  },

  updateLevelOfCurrentTemplate ({ commit, state }, { modifyLevelData } = {}) {
    return new Promise((resolve) => {
      const modifyLevelIndex = modifyLevelData.level - 1
      const requestData = Object.assign({}, state.currentTemplate.level[modifyLevelIndex], modifyLevelData)
      updateTemplateLevel(state.currentTemplate.id, requestData)
        .then(res => {
          const { levelCount, isFinish, updateTime } = res.data
          commit('UPDATE_CURRENT_TEMPLATE_LEVEL_BY_INDEX', { index: modifyLevelIndex, levelData: requestData })
          commit('RESET_TEMPLATE_STATUS_AFTER_UPDATING', {
            id: state.currentTemplate.id,
            levelCount: levelCount,
            isFinish: isFinish,
            updateTime: updateTime
          })
          resolve()
        })
    })
  },

  deleteLevelOfCurrentTemplate ({ commit, state }, level) {
    return new Promise((resolve) => {
      deleteTemplateLevel(state.currentTemplate.id, level)
        .then(res => {
          const { level: levelCache, levelCount, isFinish, updateTime } = res.data
          const deleteItemIndex = state.currentTemplate.level.findIndex(item => item.level === level)
          commit('REMOVE_CURRENT_TEMPLATE_LEVEL_BY_INDEX', { index: deleteItemIndex })
          commit('RESET_TEMPLATE_STATUS_AFTER_UPDATING', {
            id: state.currentTemplate.id,
            levelCount: levelCount,
            isFinish: isFinish,
            updateTime: updateTime
          })
          commit('SET_LEVEL_CACHE', levelCache)
          resolve()
        })
    })
  },

  restoreLevelOfCurrentTemplate ({ commit, state, dispatch }) {
    return new Promise((resolve, reject) => {
      if (!state.levelCache) reject(new Error('[CZN error]: Level cache is null'))
      restoreTemplateLevel(state.currentTemplate.id, state.levelCache)
        .then(res => {
          const { levelCount, isFinish, updateTime } = res.data
          commit('RESET_LEVEL_CACHE')
          commit('RESET_TEMPLATE_STATUS_AFTER_UPDATING', {
            id: state.currentTemplate.id,
            levelCount: levelCount,
            isFinish: isFinish,
            updateTime: updateTime
          })
          dispatch('setCurrentTemplate', state.currentTemplate.id)
          resolve()
        })
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/stores/modules/template.js